import Modal from "components/modal";
import * as notify from "shared/notify";
import { useLookups } from "shared/hooks/api/use-lookups";
import { Select } from "components/form-inputs/inputs";
import { useForm, Controller, useWatch } from "react-hook-form";
import { usePackageTypes } from "shared/hooks/api/use-package-type";
import MultiSelectList from "components/form-inputs/custom/react-multi-select";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ShotType } from "./shot-type";
import { mapBulkUploadTemplate } from "components/photography/lib/photoData";
import { Radio } from "../../../form-inputs/inputs/radio";
import { useBulkUpload } from "../../../../shared/hooks/api/use-bulk-upload";

const radioValues = {
    overwrite: "overwrite",
    noOverwrite: "noOverwrite",
};

export const BulkUploadModal = ({ open, onClose, onUploadSuccess }) => {
    const { mutate: bulkUpload } = useBulkUpload();
    const { handleSubmit, control } = useForm({
        defaultValues: {
            editingSource: 2,
            shouldOverwrite: radioValues.overwrite,
        },
    });
    const { data: lookups } = useLookups({ enabled: open, regularShotFilterKey: "inBulkUpload" });
    const { data: packageTypes } = usePackageTypes({ enabled: open });
    const [regularShots, setRegularShot] = useState([]);
    const [shotGroups, setShotGroups] = useState([]);
    const [packageTypeShots, setPackageTypeShots] = useState([]);
    const editingSource = useWatch({
        control,
        name: "editingSource",
    });
    const packageType = useWatch({
        control,
        name: "packageType",
    });

    useEffect(() => {
        if (packageType) {
            const packageTypeDetails = lookups.packageTypes.find(({ id }) => packageType === id);

            if (packageTypeDetails) {
                setPackageTypeShots(packageTypeDetails.imageViews);
            } else {
                notify.error(`"${packageTypeDetails}" wasn't found in the dataset`);
            }
        }
    }, [packageType, lookups]);

    const isUploadButtonDisabled = !editingSource || (regularShots.length === 0 && shotGroups.length === 0 && !packageType);

    const regularShotsOptions = useMemo(() => {
        if (lookups.imageViews) {
            return lookups.imageViews.map(({ imageViewCode, imageViewDescription }) => {
                return {
                    text: imageViewDescription,
                    value: imageViewCode,
                };
            });
        }

        return [];
    }, [lookups.imageViews]);

    const onSubmit = async (values) => {
        const allImageViews = [...regularShots, ...shotGroups, ...packageTypeShots];
        const uniqueImageViews = allImageViews.filter(({ shortName }, index) => {
            return index === allImageViews.findIndex((imageView) => shortName === imageView.shortName);
        });

        const wipTemplate = mapBulkUploadTemplate(uniqueImageViews);

        bulkUpload(
            {
                payload: {
                    packageTypeCode: values.packageType,
                    editingSourceId: parseInt(values.editingSource),
                    wipTemplate: wipTemplate,
                    shouldOverride: values.shouldOverwrite === radioValues.overwrite,
                },
            },
            {
                onSuccess: onUploadSuccess,
            }
        );
    };

    const findRelevantImageViews = useCallback(
        (code) => {
            const group = lookups.groups.find(({ groupCode }) => groupCode === code);

            return group ? group.imageViews : [];
        },
        [lookups.groups]
    );

    const handleAddShotGroup = useCallback(
        (onChange) => (name, values) => {
            if (!values) {
                return;
            }

            onChange(values);

            const relevantShotGroup = findRelevantImageViews(values.value);
            let newGroup = [];

            relevantShotGroup.forEach((shotGroupItem) => {
                const isAlreadyAdded = shotGroups.some((item) => shotGroupItem.name === item.name);

                if (!isAlreadyAdded) {
                    newGroup.push(shotGroupItem);
                }
            });

            if (newGroup.length === 0) {
                return;
            }

            setShotGroups((state) => [...state, ...newGroup]);
        },
        [findRelevantImageViews, shotGroups]
    );

    const handleAddRegularShot = useCallback(
        (onChange) => (name, values) => {
            if (!values) {
                return;
            }

            onChange(values);
            const imageView = lookups.imageViews.find(({ shortName }) => shortName === values.value);
            const regularShotExists = regularShots.some(({ shortName }) => shortName === values.value);

            if (regularShotExists) {
                return;
            }

            setRegularShot((state) => [...state, imageView]);
        },
        [lookups.imageViews, regularShots]
    );

    const handleRemoveRegularShot = useCallback((item) => {
        setRegularShot((state) =>
            state.filter((shot) => {
                return shot.name !== item;
            })
        );
    }, []);

    const handleRemoveGroupShot = useCallback((item) => {
        setShotGroups((state) =>
            state.filter((shotGroupItem) => {
                return shotGroupItem.name !== item;
            })
        );
    }, []);

    return (
        <Modal
            cssClass="bulk-upload-image"
            show={open}
            onClose={onClose}
        >
            <h2 className="p-16 m-0">Bulk Upload Images</h2>
            <p className="p-t-8 p-l-16 p-r-16 p-b-24 m-0">
                Specify the Package Type and/or other properties of the photos that you will bulk upload.
            </p>
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="p-l-16 p-r-16">
                    <Controller
                        name="packageType"
                        control={control}
                        render={({ field }) => {
                            return (
                                <div>
                                    <Select
                                        label="Package Type"
                                        name={field.name}
                                        value={field.value}
                                        groups={packageTypes}
                                        updateOnChange={(name, value) => field.onChange(value)}
                                        required={true}
                                    />
                                    {!packageType && <div className="error-message m-b-8">Required Field</div>}
                                </div>
                            );
                        }}
                    />

                    <Controller
                        name="groups"
                        control={control}
                        render={({ field }) => {
                            return (
                                <div className="form-field">
                                    <label>Shot Groups:</label>
                                    <MultiSelectList
                                        multi={false}
                                        autoCloseClassName=".advanced-filters-body"
                                        name={field.name}
                                        options={lookups.groups}
                                        updateOnChange={handleAddShotGroup(field.onChange)}
                                    >
                                        {shotGroups.map((group) => {
                                            return (
                                                <ShotType
                                                    key={group.name}
                                                    name={group.name}
                                                    onRemove={handleRemoveGroupShot}
                                                />
                                            );
                                        })}
                                    </MultiSelectList>
                                </div>
                            );
                        }}
                    />
                    <Controller
                        name="shots"
                        control={control}
                        render={({ field }) => {
                            return (
                                <div className="form-field">
                                    <label>Add Regular Shot:</label>
                                    <MultiSelectList
                                        multi={false}
                                        autoCloseClassName=".advanced-filters-body"
                                        name={field.name}
                                        options={regularShotsOptions}
                                        updateOnChange={handleAddRegularShot(field.onChange)}
                                    >
                                        {regularShots.map((shot) => {
                                            return (
                                                <ShotType
                                                    key={shot.name}
                                                    name={shot.name}
                                                    onRemove={handleRemoveRegularShot}
                                                />
                                            );
                                        })}
                                    </MultiSelectList>
                                </div>
                            );
                        }}
                    />
                    <Controller
                        name="editingSource"
                        control={control}
                        render={({ field }) => {
                            return (
                                <Select
                                    label="Editing Source"
                                    name={field.name}
                                    value={field.value}
                                    options={lookups.editingSources}
                                    updateOnChange={(name, value) => field.onChange(value)}
                                />
                            );
                        }}
                    />

                    <p className="p-t-8 p-r-16 p-b-8 m-0">If image already exists in an image shot:</p>
                    <div className="flex-column">
                        <Controller
                            name="shouldOverwrite"
                            control={control}
                            render={({ field }) => {
                                return (
                                    <Radio
                                        label="Override with uploaded image"
                                        name={field.name}
                                        value={radioValues.overwrite}
                                        checked={field.value === radioValues.overwrite}
                                        onChange={(e) => field.onChange(e.target.value)}
                                    />
                                );
                            }}
                        />
                        <Controller
                            name="shouldOverwrite"
                            control={control}
                            render={({ field }) => {
                                return (
                                    <Radio
                                        label="Keep current image (ignore image upload for that image shot)"
                                        name={field.name}
                                        value={radioValues.noOverwrite}
                                        checked={field.value === radioValues.noOverwrite}
                                        onChange={(e) => field.onChange(e.target.value)}
                                    />
                                );
                            }}
                        />
                    </div>
                </div>

                <div className="button-group border-top -right m-t-16 p-l-16 p-r-16">
                    <div className="flex m-t-10 m-b-4">
                        <button
                            type="button"
                            className="button -no-margin"
                            onClick={onClose}
                        >
                            Cancel
                        </button>
                        <button
                            disabled={isUploadButtonDisabled}
                            type="submit"
                            className="button -primary -no-margin"
                        >
                            Bulk Upload and Assign Images
                        </button>
                    </div>
                </div>
                <p className="txt-right txt-annotation m-0 p-l-16 p-r-16 m-b-16">Each image files size must be less than 10MB</p>
            </form>
        </Modal>
    );
};
